import { useEffect, useState } from "react";
import { BarChart, Bar, ResponsiveContainer, Tooltip, XAxis, Legend } from "recharts";
import { Loader2, Wallet, TrendingUp, Clock, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PageHeader, StatCard } from "@/components/common/ui";

export default function Finance() {
  const [data, setData] = useState(null);
  const load = () => api.get("/finance/overview").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const approve = async (id) => { try { await api.post(`/finance/invoices/${id}/approve`); toast.success("Invoice approved"); load(); } catch (e) { toast.error("Failed"); } };
  if (!data) return <div className="flex h-64 items-center justify-center text-muted-foreground"><Loader2 className="animate-spin" /></div>;

  const k = data.kpis;
  const trend = (data.trend?.months || []).map((m, i) => ({ month: m, Revenue: data.trend?.revenue?.[i] ?? 0, Expenses: data.trend?.expenses?.[i] ?? 0 }));
  const BS = { "on-track": "#12B76A", watch: "#F79009", over: "#F04438" };

  return (
    <div>
      <PageHeader overline="Finance · Module 3 · Financial Intelligence" title="Financial Intelligence"
        description="Budgets, cash flow, overtime and invoices with AI anomaly detection and program-level analysis." />
      <div className="mb-6 grid grid-cols-2 gap-4 md:grid-cols-4 lg:grid-cols-5">
        <StatCard icon={Wallet} label="Annual Budget" value={`$${(k.total_budget / 1000).toFixed(0)}k`} accent="#155EEF" index={0} />
        <StatCard icon={TrendingUp} label="Spent YTD" value={`$${(k.spent_ytd / 1000).toFixed(0)}k`} accent="#7C3AED" index={1} />
        <StatCard icon={TrendingUp} label="Utilization" value={`${k.utilization_pct}%`} accent={k.utilization_pct > 90 ? "#F04438" : "#12B76A"} index={2} />
        <StatCard icon={Clock} label="Overtime Cost" value={`$${(k.overtime_cost / 1000).toFixed(0)}k`} accent="#F79009" index={3} />
        <StatCard icon={AlertTriangle} label="Flagged Invoices" value={k.flagged} accent="#F04438" index={4} />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        <div className="lg:col-span-7 rounded-xl border bg-card p-5 shadow-sm">
          <div className="mb-3 text-sm font-bold tracking-tight">Cash Flow — Revenue vs Expenses</div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={trend} margin={{ left: -10, right: 8, top: 8, bottom: 0 }}>
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ borderRadius: 10, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))", fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="Revenue" fill="#12B76A" radius={[4, 4, 0, 0]} />
              <Bar dataKey="Expenses" fill="#155EEF" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="lg:col-span-5 rounded-xl border bg-card p-5 shadow-sm">
          <div className="mb-3 text-sm font-bold tracking-tight">Program Budget Variance</div>
          <div className="space-y-3">
            {data.budgets.map((b) => {
              const pct = Math.min(100, Math.round(b.spent_ytd / b.annual_budget * 100));
              return (
                <div key={b.id} data-testid={`budget-${b.program_id}`}>
                  <div className="mb-1 flex items-center justify-between text-xs"><span className="font-medium">{b.program_name}</span>
                    <span className="font-semibold" style={{ color: BS[b.status] }}>{b.variance_pct > 0 ? "+" : ""}{b.variance_pct}%</span></div>
                  <div className="h-2 w-full rounded-full bg-muted"><div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: BS[b.status] }} /></div>
                  <div className="mt-0.5 text-[11px] text-muted-foreground">${(b.spent_ytd / 1000).toFixed(0)}k of ${(b.annual_budget / 1000).toFixed(0)}k</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <h2 className="mb-3 mt-8 text-lg font-bold tracking-tight">Invoices</h2>
      <div className="overflow-hidden rounded-xl border bg-card shadow-sm">
        <div className="careiq-scroll overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr><th className="px-4 py-3">Vendor</th><th className="px-4 py-3">Category</th><th className="px-4 py-3">Program</th><th className="px-4 py-3">Amount</th><th className="px-4 py-3">Due</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Action</th></tr>
            </thead>
            <tbody className="divide-y">
              {data.invoices.map((inv) => (
                <tr key={inv.id} data-testid={`invoice-${inv.id}`} className="hover:bg-muted/40">
                  <td className="px-4 py-3 font-medium">{inv.vendor} {inv.flagged && <span className="ml-1 rounded bg-[#F0443815] px-1.5 py-0.5 text-[10px] font-semibold text-[#F04438]">AI FLAG</span>}
                    {inv.flagged && <div className="text-[11px] text-[#F04438]">{inv.flag_reason}</div>}</td>
                  <td className="px-4 py-3 text-muted-foreground">{inv.category}</td><td className="px-4 py-3 text-muted-foreground text-xs">{inv.program_name}</td>
                  <td className="px-4 py-3 font-semibold">${inv.amount.toLocaleString()}</td><td className="px-4 py-3 text-muted-foreground">{inv.due_date}</td>
                  <td className="px-4 py-3"><span className="rounded-full px-2 py-0.5 text-xs font-semibold capitalize" style={{ color: inv.status === "pending" ? "#F79009" : "#12B76A", backgroundColor: (inv.status === "pending" ? "#F79009" : "#12B76A") + "1a" }}>{inv.status}</span></td>
                  <td className="px-4 py-3 text-right">
                    {inv.status === "pending" ? <button data-testid={`approve-${inv.id}`} onClick={() => approve(inv.id)} className="flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs font-medium hover:border-[#12B76A] hover:text-[#12B76A] ml-auto"><CheckCircle2 size={13} /> Approve</button>
                      : <span className="text-xs text-muted-foreground">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
